import { NekoStaffSdk } from 'neko-common';
import { nekoStaff } from 'public-contracts';
import { configs } from '../configs';
import authProvider from '../authProvider';
import { usersService, User } from './usersService';

type StaffProfile = nekoStaff.StaffProfile;

const sdk = new NekoStaffSdk(configs.apiRoot);

// Use the same authentication context as the main app
sdk.createAuthClient(configs, () => {
  console.log('StaffProfileService Authentication successful');
});

// Share authentication state with the main auth provider
// Note: NekoStaffSdk doesn't have auth property, using getAuthHeader instead

// Wrapper function to handle 401 errors with token refresh
const withTokenRefresh = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  try {
    return await apiCall();
  } catch (error: any) {
    // Check if it's a 401 error
    if (error?.status === 401 || error?.message?.includes('401')) {
      console.log(
        '401 error detected in staffProfileService, attempting token refresh...'
      );

      try {
        // Attempt to refresh the token
        await authProvider.auth.fetchRefreshToken();
        console.log('Token refresh successful, retrying API call...');

        // Retry the original API call
        return await apiCall();
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, throw the original error
        throw error;
      }
    }

    // If it's not a 401 error, throw the original error
    throw error;
  }
};

export class StaffProfileService {
  private staffProfile = sdk.staffProfile;
  // Cache for active user IDs to avoid redundant API calls
  private activeUserIdsCache: Set<string> | null = null;
  private activeUserIdsCacheTime: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async getStaffProfiles(params?: {
    size?: number;
    page?: number;
    filter?: any;
    excludeArchivedUsers?: boolean; // Filter out profiles for archived/deleted users
  }): Promise<StaffProfile[]> {
    return withTokenRefresh(async () => {
      // Ensure we always fetch enough profiles - default to 1000 if not specified
      // This prevents the API from defaulting to size=10
      const fetchParams = {
        page: params?.page ?? 1,
        size: params?.size ?? 1000, // Default to 1000 to get all profiles
        ...(params?.filter && { filter: params.filter }),
      };

      // Type assertion needed because TypeScript may cache the old signature
      const profiles = (await (this.staffProfile.getStaffProfiles as any)(
        fetchParams
      )) as StaffProfile[];

      // Filter out profiles for archived/deleted users if requested
      if (params?.excludeArchivedUsers) {
        try {
          // Use cached active user IDs if available and fresh
          let activeUserIds: Set<string>;
          const now = Date.now();

          if (
            this.activeUserIdsCache &&
            now - this.activeUserIdsCacheTime < this.CACHE_TTL
          ) {
            activeUserIds = this.activeUserIdsCache;
          } else {
            // Fetch all active users (non-deleted, active)
            const activeUsers = await usersService.getUsers({
              page: 1,
              size: 1000,
            });
            activeUserIds = new Set(
              activeUsers
                .filter(
                  (u: User) => u.isActive !== false && !(u as any).deletedAt // Exclude deleted users
                )
                .map((u: User) => u.id)
            );
            // Cache the result
            this.activeUserIdsCache = activeUserIds;
            this.activeUserIdsCacheTime = now;
          }

          // Filter profiles to only include those for active users
          return profiles.filter((p: StaffProfile) =>
            activeUserIds.has(p.userId)
          );
        } catch (error) {
          console.error(
            'Failed to filter archived users from staff profiles:',
            error
          );
          // If filtering fails, return all profiles (better than returning nothing)
          return profiles;
        }
      }

      return profiles;
    });
  }

  async getStaffProfileById(id: string): Promise<StaffProfile> {
    return withTokenRefresh(async () => {
      const response = await this.staffProfile.getStaffProfile(id);
      return response;
    });
  }

  async getStaffProfileByUserId(userId: string): Promise<StaffProfile> {
    return withTokenRefresh(async () => {
      // Get all profiles with a large size to ensure we get all profiles
      // CRITICAL: API requires BOTH page AND size parameters!
      // Exclude archived/deleted users by default
      const profiles = await this.getStaffProfiles({
        page: 1,
        size: 1000,
        excludeArchivedUsers: true, // Filter out profiles for archived/deleted users
      });
      const matchingProfiles = profiles.filter(
        (p: StaffProfile) => p.userId === userId
      );

      if (matchingProfiles.length === 0) {
        throw new Error('Staff profile not found');
      }

      // If multiple profiles exist, this is a data integrity issue
      // Return the most complete one (by score) as a temporary workaround
      if (matchingProfiles.length > 1) {
        console.error(
          `❌ ERROR: Found ${matchingProfiles.length} staff profiles for userId ${userId}. This violates the one-profile-per-user constraint.`,
          matchingProfiles.map((p) => ({
            id: p.id,
            createdAt: (p as any).createdAt,
          }))
        );
        console.error(
          `   Please run the merge script to fix: npx tsx scripts/merge-duplicate-staff-profiles.ts <user-email>`
        );

        // Score profiles by completeness and return the best one
        const scoredProfiles = matchingProfiles.map((profile: StaffProfile) => {
          const metadata = (profile as any).metadata || {};
          let score = 0;

          // Basic fields
          if (profile.address) score += 1;
          if (profile.postionId) score += 1;
          if (profile.departmentId) score += 1;
          if (profile.ratePerHour && profile.ratePerHour > 0) score += 2;

          // Employment fields (check both metadata and direct fields)
          const employmentType =
            metadata.employmentType || (profile as any).employmentType;
          if (employmentType) score += 3;

          const baseSalary = metadata.baseSalary || (profile as any).baseSalary;
          if (baseSalary && baseSalary > 0) score += 3;

          const guaranteedHours =
            metadata.guaranteedHoursPerWeek ||
            (profile as any).guaranteedHoursPerWeek;
          if (guaranteedHours && guaranteedHours > 0) score += 2;

          const vacationDays =
            metadata.vacationDaysPerYear ||
            (profile as any).vacationDaysPerYear;
          if (vacationDays !== undefined && vacationDays !== null) score += 1;

          // Prefer more recent profiles if scores are equal
          const createdAt = new Date((profile as any).createdAt || 0).getTime();
          const updatedAt = new Date((profile as any).updatedAt || 0).getTime();

          return { profile, score, createdAt, updatedAt };
        });

        // Sort by score (descending), then by update date (descending), then creation date (descending)
        scoredProfiles.sort(
          (
            a: {
              profile: StaffProfile;
              score: number;
              createdAt: number;
              updatedAt: number;
            },
            b: {
              profile: StaffProfile;
              score: number;
              createdAt: number;
              updatedAt: number;
            }
          ) => {
            if (b.score !== a.score) {
              return b.score - a.score; // Higher score first
            }
            if (b.updatedAt !== a.updatedAt) {
              return b.updatedAt - a.updatedAt; // More recently updated first
            }
            return b.createdAt - a.createdAt; // More recently created first if scores equal
          }
        );

        const bestProfile = scoredProfiles[0];
        console.warn(
          `   ⚠️  Returning profile ${bestProfile.profile.id} (score: ${
            bestProfile.score
          }, updated: ${new Date(bestProfile.updatedAt).toISOString()})`
        );

        return bestProfile.profile;
      }

      return matchingProfiles[0];
    });
  }

  /**
   * Get all staff profiles for a specific userId (to detect duplicates)
   */
  async getAllStaffProfilesByUserId(userId: string): Promise<StaffProfile[]> {
    return withTokenRefresh(async () => {
      // Get all profiles with a large size to ensure we get all profiles
      // CRITICAL: API requires BOTH page AND size parameters!
      // Exclude archived/deleted users by default
      const profiles = (await this.getStaffProfiles({
        page: 1,
        size: 1000,
        excludeArchivedUsers: true, // Filter out profiles for archived/deleted users
      })) as StaffProfile[];
      return profiles.filter((p: StaffProfile) => p.userId === userId);
    });
  }

  async createStaffProfile(
    data: Omit<StaffProfile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<StaffProfile> {
    return withTokenRefresh(async () => {
      const response = await this.staffProfile.createStaffProfile(data);
      return response;
    });
  }

  async updateStaffProfile(
    id: string,
    data: Partial<Omit<StaffProfile, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<StaffProfile> {
    return withTokenRefresh(async () => {
      const response = await this.staffProfile.updateStaffProfile(id, {
        id,
        ...data,
      });
      return response;
    });
  }

  async deleteStaffProfile(id: string): Promise<void> {
    return withTokenRefresh(async () => {
      await this.staffProfile.deleteStaffProfile(id);
    });
  }
}

export const staffProfileService = new StaffProfileService();
